import { useEffect, useRef, useState } from 'react';
import './ChannelPicker.css';

/**
 * A pill-based multi-select: shows the current selection as removable
 * pills, plus a search box whose dropdown lists the not-yet-selected
 * options (filtered as you type). Fully controlled - the parent owns
 * `selected` and decides what happens on add/remove (e.g. cascading
 * selection between "default" and "allowed" channel lists).
 */
export default function ChannelPicker({ label, options, selected, onAdd, onRemove }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const availableOptions = options.filter(
    (option) =>
      !selected.includes(option) &&
      option.toLowerCase().includes(query.trim().toLowerCase())
  );

  function handleSelectOption(option) {
    onAdd(option);
    setQuery('');
    setOpen(false);
  }

  return (
    <div className="channel-picker" ref={rootRef}>
      <p className="channel-picker__label">{label}</p>

      <div className="channel-picker__pills">
        {selected.length === 0 && (
          <span className="channel-picker__placeholder">NONE SELECTED</span>
        )}
        {selected.map((item) => (
          <span className="channel-pill" key={item}>
            {item}
            <button
              type="button"
              className="channel-pill__remove"
              aria-label={`Remove ${item}`}
              onClick={() => onRemove(item)}
            >
              &times;
            </button>
          </span>
        ))}
      </div>

      <div className="channel-picker__search">
        <input
          type="text"
          className="channel-picker__input"
          placeholder="ADD A CHANNEL..."
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
        />

        {open && (
          <div className="channel-picker__dropdown" role="listbox">
            {availableOptions.length === 0 ? (
              <p className="channel-picker__empty">
                {options.every((option) => selected.includes(option))
                  ? 'ALL CHANNELS ADDED'
                  : 'NO MATCHES'}
              </p>
            ) : (
              availableOptions.map((option) => (
                <button
                  type="button"
                  key={option}
                  role="option"
                  aria-selected="false"
                  className="channel-picker__option"
                  onClick={() => handleSelectOption(option)}
                >
                  {option}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
